import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import PropTypes from 'prop-types';

import { useFetch } from 'hooks';
import { matchPath } from 'utils';

// Local components
import * as api from './api';
import BannerCarousel from './components/BannerCarousel';

const GLOBAL_SEVERITIES = ['error', 'warning'];

const getSeverityPriority = banner => GLOBAL_SEVERITIES.indexOf(banner?.severity);

const getPlacement = banner => (getSeverityPriority(banner) >= 0 ? 'global' : 'page');

function CommunicationBanners({ type, slug, placement, ...rest }) {
  // Hooks
  const { pathname } = useLocation();

  // Fetch
  const { data, mutate } = useFetch([type && slug ? api.endpoints?.[type]?.list(slug) : null], {
    revalidateOnFocus: true,
    dedupingInterval: 30 * 1000,
    refreshInterval: 10 * 60 * 1000,
  });

  // Constants
  const activeBanners = useMemo(() => {
    const rawBanners = Array.isArray(data?.results) ? data.results : [];
    return rawBanners
      .filter(
        banner => getPlacement(banner) === placement && matchPath(pathname, banner?.paths || []),
      )
      .sort((a, b) => getSeverityPriority(a) - getSeverityPriority(b));
  }, [data?.results, pathname, placement]);

  // Actions
  const handleInteraction = useCallback(
    (uid, action) =>
      api.sendInteraction({ type, slug, objectUid: uid, action }).then(() => mutate()),
    [type, slug, mutate],
  );

  // Render
  if (type !== 'sellers' && type !== 'institutions') return null;
  if (!slug || activeBanners.length === 0) return null;

  return (
    <BannerCarousel
      key={pathname}
      type={type}
      slug={slug}
      banners={activeBanners}
      onInteraction={handleInteraction}
      {...rest}
    />
  );
}

CommunicationBanners.propTypes = {
  type: PropTypes.oneOf(['sellers', 'institutions']).isRequired,
  slug: PropTypes.string,
  placement: PropTypes.oneOf(['global', 'page']),
};

CommunicationBanners.defaultProps = {
  placement: 'page',
};

export default CommunicationBanners;
